/**
 * Shared task-instance step-field validation.
 *
 * The rules live in each field's `validation_rules_snapshot` (JSON, possibly a
 * string). Both the customer onboarding view and the admin task-instance view
 * import these helpers so the two paths cannot drift — previously the admin
 * view had no rule validation, letting admins bypass field regexes.
 */

export function getValidationRules(field) {
  const rules = field.validation_rules_snapshot
  if (!rules) return {}
  try {
    return typeof rules === 'string' ? JSON.parse(rules) : rules
  } catch {
    return {}
  }
}

export function getFieldOptions(field) {
  const parsed = getValidationRules(field)
  return parsed?.options || []
}

/**
 * Validate a single field's value against its snapshot rules.
 * Returns an array of human-readable error strings (empty = valid).
 */
export function validateFieldRules(field, value) {
  const rules = getValidationRules(field)
  const type = field.field_type_snapshot
  const errors = []

  if (value === null || value === undefined || value === '') return errors

  if (type === 'TEXT' || type === 'TEXTAREA') {
    const str = String(value)
    if (rules.min_length && str.length < rules.min_length) errors.push(`Must be at least ${rules.min_length} characters`)
    if (rules.max_length && str.length > rules.max_length) errors.push(`Must be no more than ${rules.max_length} characters`)
    if (rules.pattern) {
      try {
        if (!new RegExp(rules.pattern).test(str)) errors.push(rules.pattern_message || 'Invalid format')
      } catch { /* ignore invalid regex */ }
    }
  }

  if (type === 'NUMBER' || type === 'CURRENCY') {
    const num = parseFloat(value)
    if (isNaN(num)) {
      errors.push('Must be a valid number')
    } else {
      if (rules.min_value != null && num < rules.min_value) errors.push(`Must be at least ${rules.min_value}`)
      if (rules.max_value != null && num > rules.max_value) errors.push(`Must be no more than ${rules.max_value}`)
      if (type === 'NUMBER' && rules.allow_decimals === false && !Number.isInteger(num)) errors.push('Decimals are not allowed')
    }
  }

  if (type === 'DATE' && value) {
    const d = new Date(value)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (rules.allow_past === false && d < today) errors.push('Past dates are not allowed')
    if (rules.allow_future === false && d > today) errors.push('Future dates are not allowed')
    if (rules.min_date && d < new Date(rules.min_date)) errors.push(`Date must be on or after ${new Date(rules.min_date).toLocaleDateString('en-GB')}`)
    if (rules.max_date && d > new Date(rules.max_date)) errors.push(`Date must be on or before ${new Date(rules.max_date).toLocaleDateString('en-GB')}`)
  }

  if (type === 'MULTI_SELECT' && Array.isArray(value)) {
    if (rules.min_selections && value.length < rules.min_selections) errors.push(`Select at least ${rules.min_selections}`)
    if (rules.max_selections && value.length > rules.max_selections) errors.push(`Select no more than ${rules.max_selections}`)
  }

  return errors
}
